import * as Phaser from 'phaser';
import type { HighScore } from '../../sim/highScores/index.ts';
import { cloneMapDocument } from '../../sim/map/index.ts';
import type { MatchMovement } from '../../sim/match/index.ts';
import { cloneBoatDocument, createBoat, type BoatDocument } from '../../sim/boat/index.ts';
import type { SeatController } from '../../sim/bot/seats.ts';
import { hydrateRaceRuns, loadHighScores } from '../../ui/play/highScoresStore.ts';
import type { MapSelect } from '../../ui/start/mapSelect.tsx';
import { mountMapSelect } from '../../ui/start/mapSelect.tsx';
import { mountOverlay, showOverlay } from '../../ui/overlay.tsx';
import { hydratePlayDebugDefaultsFromRepo } from '../debugDefaults.ts';
import {
  findBoatCatalogEntry,
  loadBoatCatalog,
  type BoatCatalogEntry,
} from '../boats/catalog.ts';
import { findBotCatalogEntry, loadBotCatalog, type BotCatalogEntry } from '../bots/catalog.ts';
import { cloneBotProfile, createBotProfile } from '../../sim/botProfile/index.ts';
import {
  findMapCatalogEntry,
  loadMapCatalog,
  type MapCatalogEntry,
} from '../maps/catalog.ts';
import { setPlayCursor } from '../playCursor.ts';

const START_SPLASH_SRC = '/assets/splash_2.png';
const START_CREDIT_SRC = '/assets/top-logo.png';

function decodeImage(src: string): Promise<void> {
  const image = new Image();
  image.src = src;
  return image.decode().catch(() => undefined);
}

function loadStartMenuFont(): Promise<void> {
  if (!document.fonts) {
    return Promise.resolve();
  }

  return document.fonts
    .load('42px "Bebas Neue"')
    .then(() => undefined)
    .catch(() => undefined);
}

export type StartSceneData = { view?: 'bots' };

export class StartScene extends Phaser.Scene {
  private mapSelect: MapSelect | null = null;
  private catalog: readonly MapCatalogEntry[] = [];
  private boats: readonly BoatCatalogEntry[] = [];
  private bots: readonly BotCatalogEntry[] = [];
  /** Opens on the Bots pane when coming back from the bot workshop. */
  private initialView: 'bots' | undefined;

  constructor() {
    super('StartScene');
  }

  create(data: StartSceneData = {}): void {
    this.initialView = data.view;
    setPlayCursor(true);
    const overlay = mountOverlay();
    showOverlay(overlay);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.mapSelect?.destroy();
      this.mapSelect = null;
    });

    void this.boot();
  }

  private async boot(): Promise<void> {
    await hydratePlayDebugDefaultsFromRepo();
    const [catalog, boats, bots] = await Promise.all([
      loadMapCatalog(),
      loadBoatCatalog(),
      loadBotCatalog(),
      decodeImage(START_SPLASH_SRC),
      decodeImage(START_CREDIT_SRC),
      loadStartMenuFont(),
      hydrateRaceRuns(),
    ]);

    if (!this.sys.isActive()) {
      return;
    }

    this.catalog = catalog;
    this.boats = boats;
    this.bots = bots;
    this.mountSelect(this.initialView);
    this.game.canvas.parentElement?.setAttribute('data-ready', 'true');
  }

  private mountSelect(initialView?: 'bots'): void {
    this.mapSelect?.destroy();
    this.mapSelect = mountMapSelect({
      initialView,
      maps: this.catalog.map((entry) => ({
        id: entry.id,
        label: entry.label,
        checkpointCount: entry.map.checkpoints.filter(
          (checkpoint) => !checkpoint.isGoal,
        ).length,
        laps: entry.map.laps,
        hasGoal: entry.map.checkpoints.some((checkpoint) => checkpoint.isGoal),
        topScores: loadHighScores(entry.id),
      })),
      boats: this.boats.map((entry) => ({
        id: entry.id,
        label: entry.label,
        boat: entry.boat,
      })),
      bots: this.bots.map((entry) => ({
        id: entry.id,
        label: entry.label,
        profile: entry.profile,
      })),
      onSelect: (id, competitorCount, boats, movement, boatCollisions) =>
        this.startRace(
          id,
          undefined,
          competitorCount,
          boats,
          movement,
          boatCollisions,
        ),
      onEdit: (id) => this.startEditor(id),
      onBlankEditor: () => this.startBlankEditor(),
      onEditBoat: (id) => this.startBoat(id),
      onNewBoat: () => this.startNewBoat(),
      onEditBot: (id) => {
        const entry = findBotCatalogEntry(id, this.bots);

        if (entry) {
          this.scene.start('BotScene', {
            profile: cloneBotProfile(entry.profile),
            fileName: `${entry.id}.json`,
          });
        }
      },
      onNewBot: () => this.scene.start('BotScene', { profile: createBotProfile('expert') }),
      onReplay: (id, score) => this.startRace(id, score),
    });
  }

  private startRace(
    id: string,
    replayScore?: HighScore,
    competitorCount = 1,
    boats?: readonly {
      id: string;
      boat: BoatDocument;
      name?: string;
      controller?: SeatController;
    }[],
    movement: MatchMovement = 'sequential',
    boatCollisions = true,
  ): void {
    const entry = findMapCatalogEntry(id, this.catalog);

    if (!entry) {
      return;
    }

    this.scene.start('GameScene', {
      map: cloneMapDocument(entry.map),
      mapId: entry.id,
      replayScore,
      competitorCount: replayScore ? 1 : competitorCount,
      boats: replayScore
        ? undefined
        : boats?.map((seat) => ({
            id: seat.id,
            boat: cloneBoatDocument(seat.boat),
          })),
      competitorNames: replayScore ? undefined : boats?.map((seat) => seat.name ?? ''),
      movement: replayScore ? 'sequential' : movement,
      boatCollisions: replayScore ? true : boatCollisions,
      seatControllers: replayScore
        ? undefined
        : boats?.map((seat) => seat.controller ?? 'human'),
    });
  }

  private startEditor(id: string): void {
    const entry = findMapCatalogEntry(id, this.catalog);

    if (!entry) {
      return;
    }

    this.scene.start('EditorScene', {
      map: cloneMapDocument(entry.map),
      fileName: `${entry.id}.json`,
    });
  }

  private startBlankEditor(): void {
    this.scene.start('EditorScene', {});
  }

  private startBoat(id: string): void {
    const entry = findBoatCatalogEntry(id, this.boats);

    if (!entry) {
      return;
    }

    this.scene.start('BoatScene', {
      boat: cloneBoatDocument(entry.boat),
      fileName: `${entry.id}.json`,
    });
  }

  private startNewBoat(): void {
    this.scene.start('BoatScene', { boat: createBoat() });
  }
}
